import { SkeletonItem } from "@fluentui/react-components";
import { Spinner, SpinnerSize } from "@fluentui/react";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { fetchDocumentPath } from "../../api/document/Documents";
import { DEFAULT_FOLDER_CREATION } from "../document/WithApiFileLocationProvider";
import { IconButton } from "@fluentui/react/lib/Button";
import { generateIdentifier } from "../../utils/helpers/RobotFramework";
import { Tooltip } from "./DocumentPartTree";
import { ConfirmTextField } from "../inputs/ConfirmTextField";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { sanitizeFileName } from "../../utils/helpers/SanitizeHelper";
import { getA11yHoverableProps } from "../../common/accessibility/HTMLElementsAsHoverableARIA";
import { fetchDocumentPartSuperUser, fetchNextUserRightPermission } from "../../api/document/DocumentsPart";
import { DocumentPart } from "../../api/models";
import { useAppSelector } from "../../redux/hooks/hooks";
import {
  selectAllUserDocumentCheckout,
  selectUserDocumentCheckout,
} from "../../redux/feature/settings/configurations-slice";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { UserDocumentCheckout } from "../../api/models/user-document-checkout";
import { iconCertification, iconClassName, TreeItemRowProps } from "./TreeItemRowUtils";

const LoadingMenuItemRenderer = () => (
  <div style={{ padding: "8px 16px" }}>
    <Spinner size={SpinnerSize.small} id="spinner" />
  </div>
);

const RenderMenuIcon = () => <span />;

export const TreeItemRow = (props: TreeItemRowProps) => {
  const documentPart = (props.item.data.documentPart ?? props.item.data) as DocumentPart;
  const multiLinked = documentPart?.multiLinked;
  const unaligned = documentPart?.unaligned;

  const menuProps =
    props.item.hasReadPermissions &&
    !props.isDraggable &&
    !props.isLinkExisting &&
    props.getIconButtonMenuProps?.(props.item, props.itemHierarchicalIndex, props.documentPartWithStatus);
  const documentName = props.item.text.replace(/\.docx$/, "");
  const allUserDocumentCheckouts: UserDocumentCheckout[] = useAppSelector(selectAllUserDocumentCheckout);
  const userDocumentCheckouts: UserDocumentCheckout[] = useAppSelector(selectUserDocumentCheckout);
  const isDocumentCheckout: boolean =
    userDocumentCheckouts?.length > 0 && userDocumentCheckouts[0]?.documentId === props.item.key;

  const classNames = mergeStyleSets({
    itemRowLocked: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      opacity: props.item.hasReadPermissions === false ? 0.6 : 1,
      width: "100%",
      color: "black",
      backgroundColor: isDocumentCheckout ? "#EA8F00" : "",
      borderRadius: "4px",
      padding: "4px",
    },
  });

  const rowRootRef = useRef<HTMLDivElement>(null);

  // `hasChildren` is only a lazy-loading marker: once children are fetched, it flips to false
  // while the loaded nodes are populated into `items`. Kendo's own toggle-rendering condition
  // considers both, so mirror it here to keep the id present after expansion.
  const hasExpandToggle = props.item.hasChildren || Boolean(props.item.items?.length);
  const expandToggleId = generateIdentifier(props.item.text, "expand_toggle");

  // The Kendo TreeView renders the expand/collapse chevron (span.k-treeview-toggle) as a
  // sibling of this row's content, outside of what TreeItemRow returns. Tag it with a stable,
  // deterministic id so Robot Framework E2E tests can target it to expand nested doc parts.
  useLayoutEffect(() => {
    if (!hasExpandToggle) return;
    const toggleElement = rowRootRef.current
      ?.closest(".k-treeview-item")
      ?.querySelector<HTMLElement>(":scope > div > .k-treeview-toggle");
    if (!toggleElement) return;
    toggleElement.id = expandToggleId;
    return () => {
      if (toggleElement.id === expandToggleId) {
        toggleElement.removeAttribute("id");
      }
    };
  }, [hasExpandToggle, props.item.expanded, props.item.key, expandToggleId]);

  const [timeoutInst, setTimeoutInst] = React.useState<ReturnType<typeof setTimeout>>(null);
  const [isLoadingPath, setIsLoadingPath] = React.useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    positionX: number;
    positionY: number;
  } | null>(null);
  const [tooltipMessage, setTooltipMessage] = useState<string>(null);
  const [loading, setLoading] = useState(false);

  const handleTooltipCreation = useCallback(
    (e: React.MouseEvent) => {
      if (!props.item.hasReadPermissions) return;
      setTooltipPosition((s) => ({ ...s, positionX: e.clientX, positionY: e.clientY }));
      if (!timeoutInst && !props.pathInfo.getPathForItem(props.item.key)) {
        setTimeoutInst(
          setTimeout(() => {
            setIsLoadingPath(true);
            fetchDocumentPath(props.item.key, { params: { notShowLoading: true } })
              .then((path) =>
                props.pathInfo.setPathForItem(
                  props.item.key,
                  `${DEFAULT_FOLDER_CREATION}${path}/${props.item.text.replace(/\.docx$/, "")}.docx`
                )
              )
              .finally(() => setIsLoadingPath(false));
          }, 1000)
        );
      }
    },
    [props.item, props.pathInfo]
  );

  useEffect(() => {
    if (props.pathInfo.getPathForItem(props.item.key) && timeoutInst) {
      clearTimeout(timeoutInst);
      setTimeoutInst(null);
    }
  }, [props.pathInfo, timeoutInst]);

  const handleTooltipDeletion = useCallback(() => {
    clearTimeout(timeoutInst);
    setTimeoutInst(null);
    setTooltipPosition(null);
  }, []);

  const handleDocumentNameChangeConfirm = (_oldName: string, newName: string) => {
    if (props.onDocumentNameChange) {
      props.onDocumentNameChange(props.item, newName);
    }
    if (props.setSelectedItem) {
      props.setSelectedItem(props.item);
    }
  };

  const handleDocumentNameChangeAbort = () => {
    if (props.onDocumentNameChange) {
      props.onDocumentNameChange(props.item, undefined);
    }
    if (props.setSelectedItem) {
      props.setSelectedItem(props.item);
    }
  };

  const handleMenuOpened = async () => {
    setLoading(true);
    const key = props.item.key;
    await Promise.all([
      fetchNextUserRightPermission(key).then((ua) => props.onFetchComplete(key, ua)),
      fetchDocumentPartSuperUser(key).then((su) => props.onFetchCompleteForSuperUser(key, su)),
      props.onMenuWillOpen?.(props.item),
    ]);
    setLoading(false);
  };
  const certificationMenuItems = loading
    ? [{ key: "loading", onRender: LoadingMenuItemRenderer }]
    : (props.certificationOptions?.(props.item)?.items ?? []);

  const getMenuProps =
    props.item.hasReadPermissions && certificationMenuItems.length > 0 ? { items: certificationMenuItems } : undefined;

  // Render a pulse skeleton bar for placeholder nodes shown during lazy loading
  if (props.item.nodeType === "skeleton") {
    return <SkeletonItem id="skeleton" style={{ height: 20, marginBottom: 2, width: "80%" }} animation="pulse" />;
  }

  return (
    <div
      ref={rowRootRef}
      className={classNames.itemRowLocked}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (props.item.nodeType === "pagination")
          props.fetchExpandedNode(props.itemHierarchicalIndex, false, props.item.data.number + 1);
        else props.onClick?.(e);
      }}
      onKeyDown={() => console.log("")}
    >
      {tooltipPosition && (
        <Tooltip positionY={tooltipPosition.positionY}>
          {tooltipMessage ?? props.pathInfo.getPathForItem(props.item.key) ?? documentName}
          {isLoadingPath && <Spinner size={SpinnerSize.large} id="spinner" />}
        </Tooltip>
      )}
      {(() => {
        if (props.isOutlineRibbon || props.isOpenOutlineRibbon) {
          if (props.item.key !== "key") {
            return (
              <IconButton
                id={generateIdentifier(props.item.text, "certification_button")}
                className={
                  props.documentPartWithStatus?.length > 0
                    ? iconCertification(props.item, props.documentPartWithStatus, allUserDocumentCheckouts)
                    : "loading_status"
                }
                onMenuClick={handleMenuOpened}
                menuProps={getMenuProps}
                onRenderMenuIcon={RenderMenuIcon}
                style={{ color: "var(--color-black)", float: "right", height: "20px" }}
              />
            );
          }
          return null;
        }
        return <span style={{ color: "blue" }} className={iconClassName(props.item)} key={`${props.item.key}_label`} />;
      })()}

      {props.item.renaming ? (
        <ConfirmTextField
          borderless
          placeholder={formatMessage("cof.createdocument.enterdocumentname")}
          key={`${props.item.key}_input`}
          aria-label={generateIdentifier(props.item.text, "group")}
          initialValue={documentName}
          sanitizeInput={sanitizeFileName}
          onUnmodifiedBehavior="cancel"
          onBlurBehavior="cancel"
          onConfirm={handleDocumentNameChangeConfirm}
          onCancel={handleDocumentNameChangeAbort}
          styles={{
            root: {
              width: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          }}
        />
      ) : (
        <>
          <span
            className={props.isSelected && props.item.hasReadPermissions ? "documentTreeBold" : "documentTreeRegular"}
            data-tree-item-id={props.item.key}
            style={{
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
            }}
            key={`${props.item.key}_text`}
            aria-label={generateIdentifier(props.item.text, "group")}
            {...getA11yHoverableProps({
              onHoverStart: handleTooltipCreation,
              onHoverEnd: handleTooltipDeletion,
              focusable: true,
            })}
          >
            {documentName}
          </span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            {!props.isDraggable && multiLinked && props.item.hasReadPermissions && (
              <span
                aria-label={generateIdentifier(props.item.text, "link_icon")}
                className="k-icon k-i-link-vertical k-rotate-45 k-icon-sm"
              />
            )}
            {unaligned && (
              <img
                src="/assets/warning.svg"
                alt="unaligned"
                style={{ cursor: "pointer" }}
                {...getA11yHoverableProps({
                  onHoverStart: (evt) => {
                    handleTooltipCreation(evt as React.MouseEvent);
                    setTooltipMessage(formatMessage("cof.align.unaligned.tooltip"));
                  },
                  onHoverEnd: () => {
                    handleTooltipDeletion();
                    setTooltipMessage(null);
                  },
                  focusable: true,
                })}
              />
            )}
          </div>
          {menuProps && (
            <IconButton
              id={generateIdentifier(props.item.text, "action_button")}
              iconProps={{ iconName: "MoreVertical" }}
              menuProps={menuProps}
              onRenderMenuIcon={RenderMenuIcon}
              style={{ color: "var(--color-black)", float: "right", height: "20px" }}
            />
          )}
        </>
      )}
    </div>
  );
};
